import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from './config';

const incidentsRef = collection(db, 'incidents');

// Live list of health incidents for a horse. Read access is enforced by the
// security rule (must be a member of the horse). Ongoing incidents sort first,
// then most-recent occurrence. Keeps last-known data on a transient error.
export const subscribeToIncidents = (horseId, callback) => {
  if (!horseId) { callback([]); return () => {}; }
  const q = query(incidentsRef, where('horseId', '==', horseId));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => {
      const aOngoing = a.status !== 'resolved';
      const bOngoing = b.status !== 'resolved';
      if (aOngoing !== bOngoing) return aOngoing ? -1 : 1;
      return (b.dateOccurred || '').localeCompare(a.dateOccurred || '');
    });
    callback(items);
  }, () => { /* keep last-known state on transient error */ });
};

// Live incidents across many horses (for the herd-wide calendar). Firestore
// `in` queries take up to 10 values, so we chunk the horse ids and merge.
export const subscribeToHerdIncidents = (horseIds, callback) => {
  if (!horseIds || horseIds.length === 0) { callback([]); return () => {}; }
  const chunks = [];
  for (let i = 0; i < horseIds.length; i += 10) chunks.push(horseIds.slice(i, i + 10));
  const results = chunks.map(() => []);
  const unsubs = chunks.map((chunk, idx) =>
    onSnapshot(query(incidentsRef, where('horseId', 'in', chunk)), (snap) => {
      results[idx] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const merged = results.flat();
      merged.sort((a, b) => {
        const aOngoing = a.status !== 'resolved';
        const bOngoing = b.status !== 'resolved';
        if (aOngoing !== bOngoing) return aOngoing ? -1 : 1;
        return (b.dateOccurred || '').localeCompare(a.dateOccurred || '');
      });
      callback(merged);
    }, () => {})
  );
  return () => unsubs.forEach((u) => u());
};

// Live single incident document.
export const subscribeToIncident = (incidentId, callback) => {
  if (!incidentId) { callback(null); return () => {}; }
  return onSnapshot(doc(db, 'incidents', incidentId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  }, () => { /* keep last-known state on transient error */ });
};

export const addIncident = (horseId, ownerId, data) =>
  addDoc(incidentsRef, {
    horseId,
    ownerId: ownerId || null,
    name: (data.name || '').trim(),
    tags: data.tags || [],
    dateOccurred: data.dateOccurred,
    symptoms: (data.symptoms || '').trim(),
    vetNotes: (data.vetNotes || '').trim(),
    prescribedTreatment: (data.prescribedTreatment || '').trim(),
    status: 'ongoing',
    dateResolved: null,
    photos: data.photos || [], // ready for the upload feature (see storage.js)
    logs: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Update the core incident fields (name/tags/dates/symptoms/etc.).
export const updateIncident = (incidentId, data) =>
  updateDoc(doc(db, 'incidents', incidentId), { ...data, updatedAt: serverTimestamp() });

// Mark the horse sound/healthy — resolves the incident and stamps the heal date.
export const markIncidentSound = (incidentId, dateResolved) =>
  updateDoc(doc(db, 'incidents', incidentId), {
    status: 'resolved',
    dateResolved,
    updatedAt: serverTimestamp(),
  });

// Re-open a resolved incident (issue came back).
export const reopenIncident = (incidentId) =>
  updateDoc(doc(db, 'incidents', incidentId), {
    status: 'ongoing',
    dateResolved: null,
    updatedAt: serverTimestamp(),
  });

export const deleteIncident = (incidentId) => deleteDoc(doc(db, 'incidents', incidentId));

// Append a treatment-log entry. `log` = { type, text, authorName, date }.
// `at` is a client ISO timestamp (arrayUnion can't hold a serverTimestamp).
export const addIncidentLog = (incidentId, log) =>
  updateDoc(doc(db, 'incidents', incidentId), {
    logs: arrayUnion({
      id: `log_${Date.now()}`,
      at: new Date().toISOString(),
      type: log.type || 'Note',
      text: (log.text || '').trim(),
      action: (log.action || '').trim(),
      authorName: log.authorName || '',
      date: log.date || new Date().toISOString().split('T')[0],
      photos: log.photos || [], // ready for the upload feature (see storage.js)
    }),
    updatedAt: serverTimestamp(),
  });

// Replace the whole logs array (used for editing or deleting a log entry).
export const setIncidentLogs = (incidentId, logs) =>
  updateDoc(doc(db, 'incidents', incidentId), { logs, updatedAt: serverTimestamp() });

// Attach an uploaded photo to the incident itself (the initial-injury photos).
export const addIncidentPhoto = (incidentId, photoMeta) =>
  updateDoc(doc(db, 'incidents', incidentId), {
    photos: arrayUnion(photoMeta),
    updatedAt: serverTimestamp(),
  });

// Remove a photo entry from the incident (storage file deleted by the caller).
export const removeIncidentPhoto = (incidentId, photoMeta) =>
  updateDoc(doc(db, 'incidents', incidentId), {
    photos: arrayRemove(photoMeta),
    updatedAt: serverTimestamp(),
  });
